import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Grid,
  Stack,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Send as InviteIcon,
  CheckCircle as ActiveIcon,
  Schedule as PendingIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { personsApi, personTypesApi, locationsApi } from '../../services/apiClient';
import { usePermissions } from '../../contexts/PermissionContext';
import { useSiteFilter } from '../../contexts/SiteFilterContext';
import { useIsMobile } from '../../hooks/useResponsive';
import { useThemeSettings } from '../../contexts/ThemeContext';
import PersonDialog from './components/PersonDialog';
import PersonDetailsPanel from './components/PersonDetailsPanel';
import PersonCard from './components/PersonCard';
import InviteDialog from './components/InviteDialog';

const PersonsManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();
  const isMobile = useIsMobile();
  const { t } = useThemeSettings();

  // Data state
  const [persons, setPersons] = useState([]);
  const [personTypes, setPersonTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [saving, setSaving] = useState(false);

  // Pagination & Sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('friendlyName');
  const [order, setOrder] = useState('asc');

  // Filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Selection
  const [selected, setSelected] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);

  // Dialog state
  const [personDialogOpen, setPersonDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [invitePerson, setInvitePerson] = useState(null);

  // Permissions
  const canView = hasPermission('admin') || hasPermission('persons.view');
  const canCreate = hasPermission('admin') || hasPermission('persons.create');
  const canEdit = hasPermission('admin') || hasPermission('persons.edit');
  const canDelete = hasPermission('admin') || hasPermission('persons.delete');
  const canInvite = hasPermission('admin') || hasPermission('persons.invite');

  // Fetch person types
  const fetchPersonTypes = useCallback(async () => {
    setLoadingTypes(true);
    try {
      const response = await personTypesApi.getAll({ limit: 1000 });
      if (response.data.success || response.data.data) {
        setPersonTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching person types:', error);
    } finally {
      setLoadingTypes(false);
    }
  }, []);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const response = await locationsApi.getAll({ limit: 5000 });
      if (response.data.success || response.data.data) {
        let locs = response.data.data || [];
        // Filter by site if applicable
        if (hasSiteFilter && siteLocationIds?.length > 0) {
          locs = locs.filter(l => siteLocationIds.includes(l.id));
        }
        setLocations(locs);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  }, [hasSiteFilter, siteLocationIds]);

  // Fetch persons
  const fetchPersons = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      // Apply filters
      if (filterType) {
        params.personType = filterType;
      }
      if (filterLocation) {
        params.personLocation = filterLocation;
      }

      const response = await personsApi.getAll(params);

      if (response.data.success || response.data.data) {
        let data = response.data.data || [];

        // Client-side filtering for search and status
        if (search) {
          const searchLower = search.toLowerCase();
          data = data.filter(p =>
            p.friendlyName?.toLowerCase().includes(searchLower) ||
            p.email?.toLowerCase().includes(searchLower)
          );
        }

        if (filterStatus) {
          data = data.filter(p => (p.inviteStatus || 'none') === filterStatus);
        }

        // Filter by site locations if applicable
        if (hasSiteFilter && siteLocationIds?.length > 0) {
          data = data.filter(p => !p.personLocation || siteLocationIds.includes(p.personLocation));
        }

        setPersons(data);
        setTotalCount(response.data.pagination?.total || data.length);
      }
    } catch (error) {
      console.error('Error fetching persons:', error);
      enqueueSnackbar('Failed to load persons', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterType, filterLocation, filterStatus, search, hasSiteFilter, siteLocationIds, enqueueSnackbar]);

  // Initial load
  useEffect(() => {
    fetchPersonTypes();
    fetchLocations();
  }, [fetchPersonTypes, fetchLocations]);

  useEffect(() => {
    fetchPersons();
  }, [fetchPersons]);

  // Get person type name
  const getPersonTypeName = (typeId) => {
    const type = personTypes.find(t => t.id === typeId);
    return type?.friendlyName || '-';
  };

  // Get location name
  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.friendlyName || '-';
  };

  // Get person type object
  const getPersonType = (typeId) => {
    return personTypes.find(t => t.id === typeId);
  };

  // Get location object
  const getLocation = (locationId) => {
    return locations.find(l => l.id === locationId);
  };

  // Sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort data
  const sortedPersons = useMemo(() => {
    const sorted = [...persons].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (orderBy === 'personType') {
        aValue = getPersonTypeName(a.personType);
        bValue = getPersonTypeName(b.personType);
      } else if (orderBy === 'personLocation') {
        aValue = getLocationName(a.personLocation);
        bValue = getLocationName(b.personLocation);
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [persons, orderBy, order]);

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(sortedPersons.map(p => p.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      }
      return [...prev, id];
    });
  };

  const isSelected = (id) => selected.includes(id);

  // Person dialog handlers
  const handleOpenPersonDialog = (person = null) => {
    setEditingPerson(person);
    setPersonDialogOpen(true);
  };

  const handleClosePersonDialog = () => {
    setPersonDialogOpen(false);
    setEditingPerson(null);
  };

  const handleSavePerson = async (formData) => {
    setSaving(true);
    try {
      if (editingPerson) {
        await personsApi.update(editingPerson.id, formData);
        enqueueSnackbar('Person updated successfully', { variant: 'success' });
      } else {
        await personsApi.create(formData);
        enqueueSnackbar('Person created successfully', { variant: 'success' });
      }
      handleClosePersonDialog();
      fetchPersons();
    } catch (error) {
      console.error('Error saving person:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to save person',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteDialog = (person) => {
    setDeleteTarget(person);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setSaving(true);
    try {
      await personsApi.delete(deleteTarget.id);
      enqueueSnackbar('Person deleted successfully', { variant: 'success' });
      handleCloseDeleteDialog();
      fetchPersons();
      if (selectedPerson?.id === deleteTarget.id) {
        setSelectedPerson(null);
      }
    } catch (error) {
      console.error('Error deleting person:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to delete person',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Bulk delete handlers
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;

    setSaving(true);
    try {
      await personsApi.bulkDelete(selected);
      enqueueSnackbar(`${selected.length} person(s) deleted successfully`, { variant: 'success' });
      setBulkDeleteDialogOpen(false);
      setSelected([]);
      fetchPersons();
      if (selectedPerson && selected.includes(selectedPerson.id)) {
        setSelectedPerson(null);
      }
    } catch (error) {
      console.error('Error bulk deleting persons:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to delete persons',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Invite handlers
  const handleOpenInviteDialog = (person) => {
    setInvitePerson(person);
    setInviteDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInvitePerson(null);
  };

  const handleSendInvite = async (personId, data) => {
    setSaving(true);
    try {
      await personsApi.sendInvite(personId, data);
      enqueueSnackbar('Invitation sent successfully', { variant: 'success' });
      handleCloseInviteDialog();
      fetchPersons();
    } catch (error) {
      console.error('Error sending invite:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to send invitation',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async (personId) => {
    setSaving(true);
    try {
      await personsApi.resendInvite(personId);
      enqueueSnackbar('Invitation resent successfully', { variant: 'success' });
      handleCloseInviteDialog();
      fetchPersons();
    } catch (error) {
      console.error('Error resending invite:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to resend invitation',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelInvite = async (personId) => {
    setSaving(true);
    try {
      await personsApi.cancelInvite(personId);
      enqueueSnackbar('Invitation cancelled', { variant: 'success' });
      handleCloseInviteDialog();
      fetchPersons();
    } catch (error) {
      console.error('Error cancelling invite:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to cancel invitation',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearch('');
    setFilterType('');
    setFilterLocation('');
    setFilterStatus('');
  };

  const hasFilters = search || filterType || filterLocation || filterStatus;

  // Get status chip
  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<PendingIcon sx={{ fontSize: 14 }} />}
            label="Pending"
            size="small"
            color="warning"
            variant="outlined"
          />
        );
      case 'accepted':
        return (
          <Chip
            icon={<ActiveIcon sx={{ fontSize: 14 }} />}
            label="Active"
            size="small"
            color="success"
          />
        );
      case 'expired':
        return (
          <Chip
            label="Expired"
            size="small"
            color="error"
            variant="outlined"
          />
        );
      default:
        return (
          <Chip
            label="-"
            size="small"
            variant="outlined"
          />
        );
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              Persons
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage persons and their portal access
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPersons}
            disabled={loading}
          >
            Refresh
          </Button>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPersonDialog()}
            >
              Add Person
            </Button>
          )}
          {canDelete && selected.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteDialogOpen(true)}
            >
              Delete ({selected.length})
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {personTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.friendlyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                label="Location"
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map(location => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.friendlyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="none">Not Invited</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Active User</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {hasFilters && (
            <Grid item xs={12} sm={6} md={2}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Content */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={selectedPerson && !isMobile ? 8 : 12}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : persons.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {hasFilters ? 'No persons match your filters' : 'No Persons'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {hasFilters
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Create persons to manage residents, staff, or visitors.'}
              </Typography>
              {canCreate && !hasFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenPersonDialog()}
                >
                  Add Person
                </Button>
              )}
            </Paper>
          ) : isMobile ? (
            // Mobile card view
            <Stack spacing={2}>
              {sortedPersons.map((person) => (
                <PersonCard
                  key={person.id}
                  person={person}
                  personType={getPersonType(person.personType)}
                  location={getLocation(person.personLocation)}
                  selected={isSelected(person.id)}
                  onSelect={() => handleSelect(person.id)}
                  onClick={() => setSelectedPerson(person)}
                  onEdit={() => handleOpenPersonDialog(person)}
                  onDelete={() => handleOpenDeleteDialog(person)}
                  onInvite={() => handleOpenInviteDialog(person)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  canInvite={canInvite}
                  showSelection={selected.length > 0}
                />
              ))}
            </Stack>
          ) : (
            // Desktop table view
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {canDelete && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selected.length > 0 && selected.length < sortedPersons.length}
                            checked={sortedPersons.length > 0 && selected.length === sortedPersons.length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'friendlyName'}
                          direction={orderBy === 'friendlyName' ? order : 'asc'}
                          onClick={() => handleSort('friendlyName')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'personType'}
                          direction={orderBy === 'personType' ? order : 'asc'}
                          onClick={() => handleSort('personType')}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'personLocation'}
                          direction={orderBy === 'personLocation' ? order : 'asc'}
                          onClick={() => handleSort('personLocation')}
                        >
                          Location
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedPersons.map((person) => {
                      const isItemSelected = isSelected(person.id);
                      const isRowSelected = selectedPerson?.id === person.id;

                      return (
                        <TableRow
                          key={person.id}
                          hover
                          selected={isRowSelected}
                          onClick={() => setSelectedPerson(person)}
                          sx={{ cursor: 'pointer' }}
                        >
                          {canDelete && (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isItemSelected}
                                onChange={() => handleSelect(person.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography fontWeight="medium">
                              {person.friendlyName}
                            </Typography>
                            {person.email && (
                              <Typography variant="body2" color="text.secondary">
                                {person.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getPersonTypeName(person.personType)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              {getLocationName(person.personLocation)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {getStatusChip(person.inviteStatus || 'none')}
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            {canInvite && person.inviteStatus !== 'accepted' && (
                              <Tooltip title={person.inviteStatus === 'pending' ? 'Resend Invite' : 'Invite'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenInviteDialog(person)}
                                >
                                  <InviteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenPersonDialog(person)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenDeleteDialog(person)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Paper>
          )}
        </Grid>

        {/* Details Panel */}
        {selectedPerson && !isMobile && (
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 80, height: 'calc(100vh - 120px)' }}>
              <PersonDetailsPanel
                person={selectedPerson}
                personType={getPersonType(selectedPerson.personType)}
                location={getLocation(selectedPerson.personLocation)}
                onClose={() => setSelectedPerson(null)}
                onEdit={() => handleOpenPersonDialog(selectedPerson)}
                onDelete={() => handleOpenDeleteDialog(selectedPerson)}
                onInvite={() => handleOpenInviteDialog(selectedPerson)}
                canEdit={canEdit}
                canDelete={canDelete}
                canInvite={canInvite}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Person Dialog */}
      <PersonDialog
        open={personDialogOpen}
        onClose={handleClosePersonDialog}
        onSave={handleSavePerson}
        person={editingPerson}
        personTypes={personTypes}
        locations={locations}
        saving={saving}
        loadingTypes={loadingTypes}
        loadingLocations={loadingLocations}
      />

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        onSend={handleSendInvite}
        onResend={handleResendInvite}
        onCancel={handleCancelInvite}
        person={invitePerson}
        location={invitePerson ? getLocation(invitePerson.personLocation) : null}
        saving={saving}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Person</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.friendlyName}"?
            This action cannot be undone.
          </DialogContentText>
          {deleteTarget?.inviteStatus === 'accepted' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This person is linked to a user account. The user account will remain but will no longer be linked to this person record.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Delete Multiple Persons</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} person(s)?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Deleting...' : `Delete ${selected.length}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PersonsManagement;
